'use client'

import * as React from "react";
import { cn } from "@/lib/utils";
import { useRouter, usePathname } from "next/navigation";
import { Users, Map, Settings, Calculator, Notebook } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { navigationConfig } from "@/lib/config/navigation";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Logo } from "@/components/Logo";
import { useIssues } from "@/lib/hooks/useIssues";

interface SidebarContextValue {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

export const SidebarContext = React.createContext<SidebarContextValue>({
  collapsed: false,
  setCollapsed: () => {},
});

export function useSidebarContext() {
  const context = React.useContext(SidebarContext);
  if (context === undefined) {
    throw new Error("useSidebarContext must be used within a SidebarProvider");
  }
  return context;
}

interface SidebarProviderProps {
  children: React.ReactNode;
  defaultCollapsed?: boolean;
}

export function SidebarProvider({ 
  children, 
  defaultCollapsed = false 
}: SidebarProviderProps) {
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed);

  const toggleCollapsed = React.useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  const value = React.useMemo(() => ({
    collapsed,
    setCollapsed
  }), [collapsed, setCollapsed]);

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
}

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {}

export function Sidebar({ className, ...props }: SidebarProps) {
  const { collapsed } = useSidebarContext();

  return (
    <aside
      className={cn(
        "relative shrink-0",
        collapsed ? "w-14 md:w-16" : "w-52 md:w-56",
        "transition-all duration-300 ease-in-out",
        className
      )}
      {...props}
    >
      {/* Outer container with gradient line */}
      <div className="sticky top-0 z-50 h-screen">
        {/* Gradient line - inverted direction */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-l from-blue-500 via-purple-500 to-pink-500" />
        
        {/* Main container with backdrop and border */}
        <div 
          className={cn(
            "relative h-full",
            "border-r border-border/40",
            "bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
          )}
        >
          {props.children}
        </div>
      </div>
    </aside>
  );
}

export function SidebarHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const { collapsed } = useSidebarContext();
  
  return (
    <div 
      className={cn(
        "relative h-16 flex items-center border-b border-border/40",
        collapsed ? "justify-center px-3" : "px-4",
        className
      )} 
      {...props} 
    >
      <div className={cn(
        "relative group",
        collapsed ? "h-10 w-10" : "h-10 w-44"
      )}>
        {/* Enhanced glow effect */}
        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 opacity-0 blur-lg transition-all duration-500 group-hover:opacity-100 -z-10" />
        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 opacity-0 blur-2xl transition-all duration-500 group-hover:opacity-100 -z-10" />
        
        {/* Logo with scale effect */}
        <div className="relative transition-all duration-300 ease-out group-hover:scale-105">
          <Logo className="h-10" />
        </div>
      </div>
    </div>
  );
}

export function SidebarContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const { collapsed } = useSidebarContext();
  
  return (
    <ScrollArea 
      className={cn(
        "flex-1 overflow-y-auto",
        collapsed ? "py-2" : "py-3",
        className
      )} 
      {...props} 
    />
  );
}

export function SidebarFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const { collapsed } = useSidebarContext();
  const router = useRouter();
  const pathname = usePathname();
  const { issues } = useIssues();
  const activeIssues = issues.filter(issue => issue.status !== 'Done' && issue.status !== 'Archived');

  return (
    <div 
      className={cn(
        "border-t border-border/40 p-3",
        collapsed ? "px-2" : "px-3",
        className
      )} 
      {...props}
    >
      <Button
        variant="ghost"
        size="sm"
        onClick={() => router.push('/issues')}
        className={cn(
          "w-full transition-all h-10",
          collapsed ? "justify-center p-0" : "justify-start px-3",
          pathname === '/issues' && "bg-primary/10 text-primary hover:bg-primary/20",
          "hover:bg-muted group relative"
        )}
      >
        <Notebook className={cn(
          "h-5 w-5 flex-shrink-0",
          pathname === '/issues' ? "text-primary" : "text-muted-foreground"
        )} />
        {!collapsed && (
          <span className="ml-3 text-sm font-medium">Issue Log</span>
        )}
        {!collapsed && activeIssues.length > 0 && (
          <span className="ml-auto bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs font-medium">
            {activeIssues.length}
          </span>
        )}
        {collapsed && activeIssues.length > 0 && (
          <span className="absolute -right-1 -top-1 h-4 w-4 text-[10px] font-medium rounded-full bg-primary text-primary-foreground flex items-center justify-center">
            {activeIssues.length}
          </span>
        )}
      </Button>
    </div>
  );
}

export function SidebarMenu({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("px-1 sm:px-1.5 md:px-2 space-y-0.5 sm:space-y-1", className)} {...props} />;
}

export function SidebarMenuItem({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("", className)} {...props} />;
}

interface SidebarMenuButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
}

export function SidebarMenuButton({ 
  className, 
  children,
  ...props 
}: SidebarMenuButtonProps) {
  const { collapsed } = useSidebarContext();
  
  return (
    <button
      className={cn(
        "w-full flex items-center rounded-md transition-colors text-xs sm:text-sm",
        collapsed ? "justify-center p-1.5 sm:p-2" : "justify-start gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2",
        "hover:bg-accent hover:text-accent-foreground",
        className
      )}
      {...props}
    >
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          if (child.type === 'span') {
            return !collapsed ? child : null;
          }
          return child;
        }
        return child;
      })}
    </button>
  );
}

export function SidebarNav() {
  const { collapsed } = useSidebarContext();
  const router = useRouter();
  const pathname = usePathname();

  const mainNavItems = navigationConfig.filter(item => item.section === 'main' && item.href !== '/issues');
  const toolsNavItems = navigationConfig.filter(item => item.section === 'tools');

  return (
    <div className={cn(
      "space-y-3",
      collapsed ? "px-2" : "px-3"
    )}>
      <nav className="space-y-1">
        {mainNavItems.map((item) => (
          <Button
            key={item.href}
            variant="ghost"
            size="sm"
            onClick={() => router.push(item.href)}
            className={cn(
              "w-full transition-all h-10",
              collapsed ? "justify-center p-0" : "justify-start px-3",
              pathname === item.href && "bg-primary/10 text-primary hover:bg-primary/20",
              "hover:bg-muted"
            )}
          >
            <item.icon className={cn(
              "h-5 w-5 flex-shrink-0",
              pathname === item.href ? "text-primary" : "text-muted-foreground"
            )} />
            {!collapsed && (
              <span className="ml-3 text-sm font-medium truncate">{item.title}</span>
            )}
          </Button>
        ))}
      </nav>

      <div>
        {!collapsed && (
          <div className="px-3 mb-2">
            <h3 className="text-xs font-medium text-muted-foreground/70">Tools</h3>
          </div>
        )}
        <nav className="space-y-1">
          {toolsNavItems.map((item) => (
            <Button
              key={item.href}
              variant="ghost"
              size="sm"
              onClick={() => router.push(item.href)}
              className={cn(
                "w-full transition-all h-10",
                collapsed ? "justify-center p-0" : "justify-start px-3",
                pathname === item.href && "bg-primary/10 text-primary hover:bg-primary/20",
                "hover:bg-muted"
              )}
            >
              <item.icon className={cn(
                "h-5 w-5 flex-shrink-0",
                pathname === item.href ? "text-primary" : "text-muted-foreground"
              )} />
              {!collapsed && (
                <span className="ml-3 text-sm font-medium truncate">{item.title}</span>
              )}
            </Button>
          ))}
        </nav>
      </div>
    </div>
  );
} 